// Wave 3.45 — Phase 1 — Jewellery Repair Workflow
// =============================================================================
// RepairUpgradeDialog
// =============================================================================
// Dialog that takes an existing Gross-mode customer_jewellery_item and
// promotes it to Detailed mode. Reuses the same line-row UI as the intake
// dialog but in a slimmer wrapper since the basics (title / customer /
// intake type) are already set on the item.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader2, Plus, Trash2, Coins, Gem, Sparkles, Circle, Pencil } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';

const EMPTY_GOLD = { purity_id: '', grams: '' };
const EMPTY_CARAT = { classification_id: '', classification_label: '', carats: '', pieces: '' };

export function RepairUpgradeDialog({ open, onOpenChange, item, companyId, onSaved }) {
  const [grossWeight, setGrossWeight] = useState('');
  const [goldLines, setGoldLines] = useState([]);
  const [diamondLines, setDiamondLines] = useState([]);
  const [colorStoneLines, setColorStoneLines] = useState([]);
  const [pearlLines, setPearlLines] = useState([]);
  const [purities, setPurities] = useState([]);
  const [diamondClass, setDiamondClass] = useState([]);
  const [colorStoneClass, setColorStoneClass] = useState([]);
  const [pearlClass, setPearlClass] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setGrossWeight(item?.incoming_gross_weight_g ?? '');
    setGoldLines((item?.gold_lines || []).map((l) => ({ ...l, grams: l.grams ?? '' })));
    setDiamondLines((item?.diamond_lines || []).map((l) => ({ ...l })));
    setColorStoneLines((item?.color_stone_lines || []).map((l) => ({ ...l })));
    setPearlLines((item?.pearl_lines || []).map((l) => ({ ...l })));
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const loadMasters = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [pur, dia, cs, pl] = await Promise.all([
        api.get('/gold/purities', { params: { company_id: companyId } }),
        api.get('/diamonds/classifications', { params: { company_id: companyId } }),
        api.get('/color-stones/classifications', { params: { company_id: companyId } }),
        api.get('/pearls/classifications', { params: { company_id: companyId } }),
      ]);
      setPurities(pur.data || []);
      setDiamondClass(dia.data || []);
      setColorStoneClass(cs.data || []);
      setPearlClass(pl.data || []);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load master data'));
    } finally {
      setLoading(false);
    }
  };

  const isUpgrade = item?.detail_mode === 'gross';

  const addGoldLine = () => setGoldLines((l) => [...l, { ...EMPTY_GOLD }]);
  const removeGoldLine = (i) => setGoldLines((l) => l.filter((_, idx) => idx !== i));
  const updateGoldLine = (i, patch) =>
    setGoldLines((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const validation = useMemo(() => {
    const any =
      goldLines.length > 0 ||
      diamondLines.length > 0 ||
      colorStoneLines.length > 0 ||
      pearlLines.length > 0;
    if (!any) return 'Add at least one component line.';
    for (const gl of goldLines) {
      if (!gl.purity_id) return 'Each gold line needs a purity';
      if (!(parseFloat(gl.grams) > 0)) return 'Each gold line needs grams > 0';
    }
    return null;
  }, [goldLines, diamondLines, colorStoneLines, pearlLines]);

  const submit = async () => {
    if (validation) {
      toast.error(validation);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        detail_mode: isUpgrade ? 'detailed' : undefined,
        incoming_gross_weight_g:
          grossWeight === '' || grossWeight === null ? null : parseFloat(grossWeight),
        gold_lines: goldLines
          .filter((g) => g.purity_id && parseFloat(g.grams) > 0)
          .map((g) => ({ purity_id: g.purity_id, grams: parseFloat(g.grams) })),
        diamond_lines: normalise(diamondLines),
        color_stone_lines: normalise(colorStoneLines),
        pearl_lines: normalise(pearlLines),
      };
      // Strip undefined keys (Pydantic literal validators dislike nulls)
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await api.patch(`/repairs/items/${item.id}`, payload);
      toast.success(isUpgrade ? 'Upgraded to detailed mode' : 'Details updated');
      onSaved?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to update repair item'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="repair-upgrade-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Pencil size={18} className="text-[var(--ink-navy)]" />
            <DialogTitle>
              {isUpgrade ? 'Upgrade to Detailed mode' : 'Edit detail lines'}
            </DialogTitle>
          </div>
          <DialogDescription>
            {isUpgrade
              ? 'Capture the full breakdown of materials for this piece. Once saved, the item moves from Gross-only to Detailed mode (cannot be reverted).'
              : 'Edit the detailed material breakdown for this repair item.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-[11px] text-slate-500">Incoming gross weight (grams)</Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={grossWeight}
              onChange={(e) => setGrossWeight(e.target.value)}
              className="h-9 font-mono tabular-nums"
              data-testid="upgrade-gross-input"
            />
          </div>
        </div>

        <div className="space-y-4 mt-3">
          {/* Gold */}
          <Block icon={<Coins size={14} className="text-amber-600" />} title="Gold components" onAdd={addGoldLine}>
            {goldLines.length === 0 ? (
              <div className="text-[11px] text-slate-500 italic">No gold lines yet.</div>
            ) : (
              <div className="space-y-2">
                {goldLines.map((gl, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`upgrade-gold-line-${i}`}>
                    <div className="col-span-7">
                      <Label className="text-[10px] text-slate-500">Purity</Label>
                      <SearchableSelect
                        items={purities || []}
                        value={gl.purity_id}
                        onValueChange={(v) => updateGoldLine(i, { purity_id: v || '' })}
                        itemToLabel={(p) => `${p.name} (${p.percent}%)`}
                        searchKeys={['name']}
                        placeholder="Select purity"
                        searchPlaceholder="Search purity…"
                      />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-[10px] text-slate-500">Grams</Label>
                      <Input
                        type="number"
                        step="0.001"
                        min="0"
                        value={gl.grams}
                        onChange={(e) => updateGoldLine(i, { grams: e.target.value })}
                        className="h-9 font-mono tabular-nums"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-2 text-rose-600 hover:bg-rose-50"
                        onClick={() => removeGoldLine(i)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Block>

          <CaratBlock
            icon={<Gem size={14} className="text-[var(--diamond-icon)]" />}
            title="Diamond components"
            classes={diamondClass}
            lines={diamondLines}
            onChange={setDiamondLines}
            testidPrefix="upgrade-diamond"
          />
          <CaratBlock
            icon={<Sparkles size={14} className="text-pink-500" />}
            title="Colour stone components"
            classes={colorStoneClass}
            lines={colorStoneLines}
            onChange={setColorStoneLines}
            testidPrefix="upgrade-color-stone"
          />
          <CaratBlock
            icon={<Circle size={14} className="text-slate-500" />}
            title="Pearl components"
            classes={pearlClass}
            lines={pearlLines}
            onChange={setPearlLines}
            testidPrefix="upgrade-pearl"
          />
        </div>

        {validation && (
          <div className="mt-3 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {validation}
          </div>
        )}

        <DialogFooter className="mt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="upgrade-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || !!validation}
            className="luxury-button"
            data-testid="upgrade-submit-button"
          >
            {submitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Pencil size={14} className="mr-1.5" />}
            {submitting ? 'Saving…' : isUpgrade ? 'Upgrade to Detailed' : 'Save details'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function normalise(lines) {
  return (lines || [])
    .filter((cl) => cl.classification_id || (cl.classification_label || '').trim())
    .map((cl) => ({
      classification_id: cl.classification_id || null,
      classification_label: cl.classification_label || '',
      carats: cl.carats ? parseFloat(cl.carats) : 0,
      pieces: cl.pieces ? parseInt(cl.pieces, 10) : 0,
    }));
}

function Block({ icon, title, onAdd, children }) {
  return (
    <div className="border border-[var(--divider)] rounded-md p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--ink-navy)]">
          {icon}
          {title}
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onAdd}>
          <Plus size={12} className="mr-1" /> Add line
        </Button>
      </div>
      {children}
    </div>
  );
}

function CaratBlock({ icon, title, classes, lines, onChange, testidPrefix }) {
  const add = () => onChange((l) => [...l, { ...EMPTY_CARAT }]);
  const remove = (i) => onChange((l) => l.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <Block icon={icon} title={title} onAdd={add}>
      {lines.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic">None yet.</div>
      ) : (
        <div className="space-y-2">
          {lines.map((cl, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 items-end"
              data-testid={`${testidPrefix}-line-${i}`}
            >
              <div className="col-span-6">
                <Label className="text-[10px] text-slate-500">Classification</Label>
                {(() => {
                  const items = [
                    ...(classes || []).map((c) => ({ id: c.id, schema_code: c.schema_code, shape: c.shape_name })),
                    { id: '__custom__', schema_code: 'Custom label…', shape: '' },
                  ];
                  return (
                    <SearchableSelect
                      items={items}
                      value={cl.classification_id || '__custom__'}
                      onValueChange={(v) =>
                        update(i, {
                          classification_id: !v || v === '__custom__' ? '' : v,
                          classification_label:
                            !v || v === '__custom__' ? cl.classification_label || '' : '',
                        })
                      }
                      itemToLabel={(c) => c.schema_code}
                      itemToSubLabel={(c) => c.shape || null}
                      searchKeys={['schema_code', 'shape']}
                      placeholder="Pick a classification"
                      searchPlaceholder="Search…"
                    />
                  );
                })()}
                {!cl.classification_id && (
                  <Input
                    value={cl.classification_label}
                    onChange={(e) => update(i, { classification_label: e.target.value })}
                    placeholder="Free-text label"
                    className="h-8 mt-1 text-xs"
                  />
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-slate-500">Carats</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={cl.carats}
                  onChange={(e) => update(i, { carats: e.target.value })}
                  className="h-9 font-mono tabular-nums"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] text-slate-500">Pieces</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={cl.pieces}
                  onChange={(e) => update(i, { pieces: e.target.value })}
                  className="h-9 font-mono tabular-nums"
                />
              </div>
              <div className="col-span-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-2 text-rose-600 hover:bg-rose-50"
                  onClick={() => remove(i)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Block>
  );
}

export default RepairUpgradeDialog;
